import { ref, watch, computed } from "@vue/composition-api";
import axios from "@/libs/axios";
import store from "@/store";
import { debounce } from "lodash";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import inventoryStatus from "./constants/inventoryStatus";
import ability from "@/libs/acl/ability";

const getDefaultFilters = () => ({
  categories: [],
  regions: [],
  units: [],
  users: [],
  countries: [],
  municipalities: [],
  status: [],
  equipmentTypes: [],
  brands: [],
  projectIds: [],
  rems: null,
  sirh: null,
  model: null,
  usefulLifeMonths: null,
  remainingLifeMonths: null,
  status: null,
  installationDate: null,
  depreciationStartDate: null,
});

export default function useList() {
  const { t } = useI18nUtils();

  const refListTable = ref(null);
  const buttonExport = ref(false);

  const perPage = ref(10);
  const currentPage = ref(1);
  const totalList = ref(0);
  const searchQuery = ref("");
  const sortBy = ref("created_at");
  const isSortDirDesc = ref(true);
  const filters = ref(getDefaultFilters());

  const perPageOptions = [10, 25, 50, 100];

  const tableColumns = [
    { key: "unit_sector", label: "inventory.fields.division", sortable: false },
    { key: "unit_region", label: "inventory.fields.region", sortable: false },
    { key: "unit_municipality", label: "inventory.fields.municipality", sortable: false },
    { key: "unit_rems", label: "inventory.fields.rems", sortable: false },
    { key: "unit_sirh", label: "inventory.fields.sirh", sortable: false },
    { key: "unit_category", label: "inventory.fields.unit_category", sortable: false },
    { key: "unit_name", label: "inventory.fields.unit_name", sortable: false },
    { key: "equipment_type", label: "inventory.fields.equipment_type", sortable: false },
    { key: "brand", label: "inventory.fields.brand", sortable: false },
    { key: "model", label: "inventory.fields.model", sortable: false },
    { key: "serial_number", label: "inventory.fields.serial_number", sortable: false },
    {
      key: "fixed_asset_number",
      label: "inventory.fields.fixed_asset_number",
      sortable: false,
    },
    {
      key: "installation_date",
      label: "inventory.fields.installation_date",
      sortable: false,
    },
    {
      key: "depreciation_start_date",
      label: "inventory.fields.depreciation_start_date",
      sortable: false,
    },
    { key: "equipment_cost", label: "inventory.fields.equipment_cost", sortable: false },
    {
      key: "accumulated_depreciation",
      label: "inventory.fields.accumulated_depreciation",
      sortable: false,
    },
    {
      key: "useful_life_months",
      label: "inventory.fields.useful_life_months",
      sortable: false,
    },
    {
      key: "remaining_life_months",
      label: "inventory.fields.remaining_life_months",
      sortable: false,
    },
    { key: "status", label: "inventory.fields.status", sortable: false },
    { key: "project_id", label: "inventory.fields.project_id", sortable: false },
    { key: "project_name", label: "inventory.fields.project_name", sortable: false },
  ];

  if (ability.can("update", "Inventory") || ability.can("delete", "Inventory")) {
    tableColumns.splice(0, 0, { label: "actions.name", key: "actions" });
  }

  const statusOptions = [
    {
      value: inventoryStatus.INSTALLED,
      text: t("inventory.status.installed"),
    },
    {
      value: inventoryStatus.NEW_CUSTODY,
      text: t("inventory.status.new_custody"),
    },
    {
      value: inventoryStatus.PAUSED,
      text: t("inventory.status.paused"),
    },
  ];

  store.dispatch("unit/getAll");
  store.dispatch("user/getAll");
  store.dispatch("region/getAll");
  store.dispatch("country/getAll");
  store.dispatch("municipality/getAll");
  store.dispatch("facility_category/getAll");
  store.dispatch("inventory_brands_catalog/getAll");
  store.dispatch("inventory_projects_catalog/getAll");
  store.dispatch("inventory_equipment_types_catalog/getAll");
  store.dispatch("unit/getAll");

  const unitOptions = computed(() => store.getters["unit/getSelectedItems"]);
  const userOptions = computed(() => store.getters["user/getSelectedItems"] || []);
  const countryOptions = computed(() => store.getters["country/getSelectedItems"]);
  const municipalityOptions = computed(
    () => store.getters["municipality/getSelectedItems"]
  );
  const regionOptions = computed(() => store.getters["region/getSelectedItems"]);
  const categoryOptions = computed(
    () => store.getters["facility_category/getSelectedItems"]
  );
  const brandOptions = computed(
    () => store.getters["inventory_brands_catalog/getSelectedItems"]
  );
  const projectOptions = computed(
    () => store.getters["inventory_projects_catalog/getSelectedItems"]
  );
  const equipmentOptions = computed(
    () => store.getters["inventory_equipment_types_catalog/getSelectedItems"]
  );
  const unitsOptions = computed(() => store.getters["unit/getSelectedItems"]);

  const clearSearchFilters = () => {
    filters.value = getDefaultFilters();
    currentPage.value = 1;
    refetchData();
  };

  const getFilters = (action = "") => ({
    "page[n]": currentPage.value,
    per_page: perPage.value,
    ...filters.value,
    action,
  });

  const buildFilters = () => {
    currentPage.value = 1;
    refetchData();
  };

  const fetchList = (ctx) =>
    new Promise((resolve, reject) => {
      const queryParams = getFilters();

      axios
        .get("/v1/inventories", { params: queryParams })
        .then((response) => {
          const { data, meta } = response.data;
          resolve(data);
          totalList.value = meta.total;
        })
        .catch((error) => reject(error));
    });

  const refetchData = () => {
    refListTable.value.refresh();
  };

  const exportExcel = async () => {
    buttonExport.value = true;
    try {
      const queryParams = getFilters("export");

      const res = await axios.get("/v1/inventories", {
        params: queryParams,
        responseType: "blob",
      });

      const cd = res.headers["content-disposition"];
      let filename = "simulacrums.xlsx";
      if (cd) {
        const m = cd.match(/filename=\"?([^\";]+)\"?/i);
        if (m) filename = decodeURIComponent(m[1]);
      }
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        component: ToastificationContent,
        props: {
          title: error?.response?.data?.message || "Error al exportar",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    } finally {
      buttonExport.value = false;
    }
  };

  watch([currentPage, perPage, sortBy, isSortDirDesc], () => {
    refetchData();
  });
  watch(
    [searchQuery],
    debounce(() => {
      refetchData();
    }, 500)
  );

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const filtersCount = computed(() => {
    let count = 0;
    if (filters.value.units.length) count++;
    if (filters.value.users.length) count++;
    if (filters.value.regions.length) count++;
    if (filters.value.categories.length) count++;
    if (filters.value.municipalities.length) count++;
    if (filters.value.rems !== null && filters.value.rems !== undefined) count++;
    if (filters.value.sirh !== null && filters.value.sirh !== undefined) count++;
    if (filters.value.unitName !== null && filters.value.unitName !== undefined) count++;
    if (filters.value.equipmentTypes.length) count++;
    if (filters.value.brands.length) count++;
    if (filters.value.projectIds.length) count++;
    if (filters.value.model !== null && filters.value.model !== undefined) count++;
    if (
      filters.value.usefulLifeMonths !== null &&
      filters.value.usefulLifeMonths !== undefined
    )
      count++;
    if (
      filters.value.remainingLifeMonths !== null &&
      filters.value.remainingLifeMonths !== undefined
    )
      count++;
    if (filters.value.status !== null && filters.value.status !== undefined) count++;
    if (
      filters.value.installationDate !== null &&
      filters.value.installationDate !== undefined
    )
      count++;
    if (
      filters.value.depreciationStartDate !== null &&
      filters.value.depreciationStartDate !== undefined
    )
      count++;
    return count;
  });

  return {
    tableColumns,
    searchQuery,
    dataMeta,
    perPage,
    currentPage,
    totalList,
    refListTable,
    refetchData,
    perPageOptions,
    fetchList,
    clearSearchFilters,
    filtersCount,
    filters,
    buildFilters,
    unitOptions,
    userOptions,
    countryOptions,
    municipalityOptions,
    regionOptions,
    categoryOptions,
    exportExcel,
    buttonExport,
    brandOptions,
    projectOptions,
    equipmentOptions,
    unitsOptions,
    statusOptions,
  };
}

